import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signupShamanSchema, type SignupShamanInput } from '@mudong/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/lib/supabase';
import { formatKoreanPhoneInput } from '@/lib/phone';
import { ko } from '@/i18n/ko';

export function SignupPage() {
  const navigate = useNavigate();
  const [serverErr, setServerErr] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SignupShamanInput>({
    resolver: zodResolver(signupShamanSchema),
  });

  const phone = watch('phone') ?? '';

  async function onSubmit(values: SignupShamanInput) {
    setServerErr(null);
    const { data, error } = await supabase.functions.invoke('signup-shaman', {
      body: values,
    });
    if (error) {
      setServerErr(error.message ?? '가입에 실패했습니다');
      return;
    }
    const access = (data as { session?: { access_token: string; refresh_token: string } } | null)
      ?.session;
    if (access) {
      await supabase.auth.setSession({
        access_token: access.access_token,
        refresh_token: access.refresh_token,
      });
    } else {
      await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });
    }
    navigate('/me/billing', { replace: true });
  }

  return (
    <div className="card space-y-5">
      <h1 className="text-2xl font-bold">{ko.auth.signup}</h1>
      <p className="text-base text-ink-muted">
        가입 후 구독 결제를 등록해야 예약 페이지가 공개됩니다.
      </p>
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label className="label" htmlFor="display_name_ko">표시 이름 (예: 달빛 보살)</label>
          <input id="display_name_ko" className="field" {...register('display_name_ko')} />
          {errors.display_name_ko && (
            <p className="text-danger text-base mt-1">{errors.display_name_ko.message}</p>
          )}
        </div>
        <div>
          <label className="label" htmlFor="slug">예약 페이지 주소 (영문)</label>
          <div className="flex items-center gap-2">
            <span className="text-ink-muted">/b/</span>
            <input
              id="slug"
              className="field"
              placeholder="moonlight"
              {...register('slug')}
            />
          </div>
          {errors.slug && (
            <p className="text-danger text-base mt-1">{errors.slug.message}</p>
          )}
        </div>
        <div>
          <label className="label" htmlFor="phone">{ko.booking.customerPhone}</label>
          <input
            id="phone"
            className="field"
            inputMode="tel"
            value={phone}
            onChange={(e) =>
              setValue('phone', formatKoreanPhoneInput(e.target.value), {
                shouldValidate: true,
              })
            }
          />
          {errors.phone && (
            <p className="text-danger text-base mt-1">{errors.phone.message}</p>
          )}
        </div>
        <div>
          <label className="label" htmlFor="email">{ko.auth.email}</label>
          <input id="email" className="field" type="email" {...register('email')} />
          {errors.email && (
            <p className="text-danger text-base mt-1">{errors.email.message}</p>
          )}
        </div>
        <div>
          <label className="label" htmlFor="password">
            {ko.auth.password} <span className="text-ink-muted">({ko.auth.passwordHelp})</span>
          </label>
          <input id="password" className="field" type="password" {...register('password')} />
          {errors.password && (
            <p className="text-danger text-base mt-1">{errors.password.message}</p>
          )}
        </div>
        {serverErr && <p className="text-danger text-base">{serverErr}</p>}
        <button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
          {isSubmitting ? ko.common.loading : ko.auth.signup}
        </button>
      </form>
    </div>
  );
}
